import { CursorGrid } from "../../shared/services"
import { SourceBackground } from "../services/SourceBackground"

import type { Art, Box, Frame } from "../types/Frame"
import type { Framing } from "../types/Framing"
import type { Options } from "../types/Options"
import type { ResolvedResampling } from "../types/Resampling"

const toCanvas = (pixels: ImageData) => {
  const canvas = document.createElement("canvas")
  canvas.width = pixels.width
  canvas.height = pixels.height
  canvas.getContext("2d")?.putImageData(pixels, 0, 0)
  return canvas
}

const covers = (outer: Box, inner: Box) =>
  outer.left <= inner.left &&
  outer.top <= inner.top &&
  outer.right >= inner.right &&
  outer.bottom >= inner.bottom

// The one box around every frame's art, so an animation doesn't jump; null when nothing is visible.
const findTrimBox = (
  frames: Frame[],
  removeBackground: boolean,
  resampling: ResolvedResampling,
) => {
  let union: Box | null = null
  for (const frame of frames) {
    // A background left in place reaches the image's edges.
    const box =
      frame.sourceBackground && !removeBackground
        ? frame.imageBox
        : frame.drawings[resampling].art?.box
    if (!box) continue

    union = union
      ? {
          left: Math.min(union.left, box.left),
          top: Math.min(union.top, box.top),
          right: Math.max(union.right, box.right),
          bottom: Math.max(union.bottom, box.bottom),
        }
      : box
  }
  return union
}

// The art scaled so the trim box fits or fills the grid.
const drawTrimmed = (art: Art, box: Box, framing: Framing, resampling: ResolvedResampling) => {
  const { left, top, right, bottom } = art.box
  if (box.left === left && box.top === top && box.right === right && box.bottom === bottom) {
    return framing === "fill" ? art.filledPixels : art.pixels
  }

  const size = CursorGrid.SIZE
  const width = box.right - box.left
  const height = box.bottom - box.top
  const scale = size / (framing === "fill" ? Math.min(width, height) : Math.max(width, height))
  // The art's pixels are its box fitted on a grid of their own.
  const side = Math.max(right - left, bottom - top)
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext("2d", { willReadFrequently: true })
  if (!context) return art.pixels

  context.imageSmoothingEnabled = resampling === "smooth"
  context.imageSmoothingQuality = "high"
  context.drawImage(
    toCanvas(art.pixels),
    ((left + right - side - box.left - box.right) / 2) * scale + size / 2,
    ((top + bottom - side - box.top - box.bottom) / 2) * scale + size / 2,
    side * scale,
    side * scale,
  )
  return context.getImageData(0, 0, size, size)
}

// A copy mirrored left to right.
const flipHorizontally = ({ data, width, height }: ImageData) => {
  const flipped = new ImageData(width, height)
  const from = new Uint32Array(data.buffer)
  const to = new Uint32Array(flipped.data.buffer)
  for (let row = 0; row < from.length; row += width) {
    for (let x = 0; x < width; x += 1) to[row + x] = from[row + width - 1 - x]
  }
  return flipped
}

// The frames as the options show and upload them, drawn with the resolved resampling; the same
// frame where no option touches it.
export const applyOptions = (
  frames: Frame[],
  options: Options,
  resampling: ResolvedResampling,
): Frame[] => {
  const trimBox = options.trim ? findTrimBox(frames, options.removeBackground, resampling) : null

  return frames.map((frame) => {
    const { pixels: fitted, filledPixels, art } = frame.drawings[resampling]
    let pixels =
      trimBox && art && !covers(trimBox, frame.imageBox)
        ? drawTrimmed(art, trimBox, options.framing, resampling)
        : options.framing === "fill"
          ? filledPixels
          : fitted

    // The color comes from the fitted image's corners, which a fill may have cropped away.
    if (options.removeBackground && frame.sourceBackground) {
      pixels = SourceBackground.cut(pixels, frame.sourceBackground)
    }

    if (options.flip) pixels = flipHorizontally(pixels)

    if (pixels === frame.pixels) return frame
    return {
      ...frame,
      pixels,
      url: toCanvas(pixels).toDataURL("image/png"),
      sourceBackground: options.removeBackground ? null : frame.sourceBackground,
    }
  })
}
